export type ApiErrorCode = 'NETWORK_ERROR' | 'TIMEOUT' | 'CANCELLED' | 'INVALID_RESPONSE' | 'HTTP_ERROR' | 'UNKNOWN'

/**
 * The one error type every API call throws, so a screen never has to know how a request failed:
 *   - NETWORK_ERROR  the server could not be reached (says nothing about whether it processed the request)
 *   - TIMEOUT        it took longer than the configured limit
 *   - CANCELLED      the caller aborted it (a query that was superseded or unmounted)
 *   - HTTP_ERROR     the server answered with an error status; `message` is the server's own wording where it gave one
 *   - INVALID_RESPONSE the server answered 2xx with something that is not the documented shape
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number | null
  /** The server's correlation id, to quote when asking the backend team about a failed call. */
  readonly requestId: string | null
  readonly details?: unknown

  constructor(message: string, options: { code?: ApiErrorCode; status?: number | null; requestId?: string | null; details?: unknown } = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = options.code ?? 'UNKNOWN'
    this.status = options.status ?? null
    this.requestId = options.requestId ?? null
    this.details = options.details
  }

  /** 4xx: retrying the same request cannot help. */
  get isClientError() {
    return this.status !== null && this.status >= 400 && this.status < 500
  }

  get isCancelled() {
    return this.code === 'CANCELLED'
  }

  get isNetworkError() {
    return this.code === 'NETWORK_ERROR' || this.code === 'TIMEOUT'
  }
}

/** Any thrown value as an `ApiError`, so `catch (error)` blocks can always show `toApiError(error).message`. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (error instanceof Error) return new ApiError(error.message)
  return new ApiError('Something went wrong.')
}

/**
 * A readable message from the backend's problem-details body: the first validation error, else `detail`, else `title`.
 * Falls back to a sentence for the status code when the body says nothing useful.
 */
export function messageFromProblem(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const problem = body as { detail?: unknown; title?: unknown; errors?: Record<string, unknown> }
    if (problem.errors && typeof problem.errors === 'object') {
      const first = Object.values(problem.errors).flat()[0]
      if (typeof first === 'string') return first
    }
    if (typeof problem.detail === 'string' && problem.detail) return problem.detail
    if (typeof problem.title === 'string' && problem.title) return problem.title
  }
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'That could not be found.'
  if (status === 409) return 'That conflicts with what is already there.'
  if (status === 413) return 'The files are too large to upload together. Send fewer or smaller files.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500) return 'The server ran into a problem. Please try again later.'
  return `The server answered with an error (${status}).`
}
